// ── Shared board-card hover expand (TPT372) ──
//
// Mirrors the Task App's own card hover growth (ai/todo/server/src/client/task-card.js
// expandCard(card, 'hover') / collapseCard()) instead of faking it with a CSS transform:
// a transform can never un-clamp `-webkit-line-clamp` or reveal text that was truncated
// before it reached the DOM, so it can grow a card without ever showing more of it. This
// turns the hovered card into a `position:fixed` overlay (escaping every ancestor's own
// `overflow` clipping — including a scrolling `.kanban-column-body`, which is what makes
// growth symmetric regardless of which board a card sits in) with an invisible
// placeholder holding its old grid/flex slot, same split as Task App's own version:
// CSS class carries the static bits (transition, shadow, un-clamp rules), JS carries the
// per-instance computed geometry (left/top/width/height), inline, same as expandCard().
const GROW_X = 12; // total width growth — small, symmetric, decorative ("a little left/right")
const TOP_GROW = 8; // constant upward bias on top — the rest of any content growth goes down
const MARGIN = 10; // px kept clear of a visible-bounds edge (matches Task App's own clamp margin)
const EXPAND_DELAY = 150; // ms hover dwell before expanding — avoids flicker on a fast mouse pass
const COLLAPSE_DELAY = 100; // ms before collapsing on mouseleave — absorbs a brief cursor blip
const SCROLL_SETTLE = 150; // ms after the last scroll event before a card may expand again
const LINE_PX = 16; // wheel deltaMode 1 (lines) -> px

const EXPANDED_CLASS = 'task-card-expanded';
const PLACEHOLDER_CLASS = 'task-card-placeholder';

// Focus ring (TPT377). `.card-focused` is what styles.css draws the 2px accent ring on: it rides
// along with an expanded card, and a clicked card is pinned with `.task-card-selected` so the
// ring survives the pointer leaving (collapseCard() only drops it from an unpinned card).
// selectCard()/clearSelectedCard() below own the pin; the page modules call them around the
// task-detail drawer's open/close.
const FOCUSED_CLASS = 'card-focused';
const SELECTED_CLASS = 'task-card-selected';

// Pure: clamp `value` into [lo, hi]. When the target itself doesn't fit (hi < lo — the
// card is taller/wider than the visible bounds), anchor to the start edge (lo) rather
// than let the clamp invert, same bias as Task App's own overlay ("expand downward,
// clamp to viewport" — keeps the card's header on screen instead of centering an
// overflow no clamp can fully solve).
function clampRange(value, lo, hi) {
  if (hi < lo) return lo;
  return Math.min(Math.max(value, lo), hi);
}

// Pure: given the card's original rect, the bounds it must stay inside, and its target
// (width, height), pick {left, top}. Growth is symmetric by default (half of the width
// growth on each side; a small constant bias upward on height, the rest — i.e. whatever
// extra height un-clamping actually needed — lands at the bottom) and only skews toward
// an edge when the symmetric position would cross it.
export function computeExpandedOrigin(rect, bounds, width, height, opts = {}) {
  const margin = opts.margin ?? MARGIN;
  const topGrow = opts.topGrow ?? TOP_GROW;

  const growX = width - rect.width;
  const idealLeft = rect.left - growX / 2;
  const idealTop = rect.top - topGrow;

  return {
    left: clampRange(idealLeft, bounds.left + margin, bounds.right - width - margin),
    top: clampRange(idealTop, bounds.top + margin, bounds.bottom - height - margin),
  };
}

// The visible area an expanded card must stay inside: the viewport, narrowed below the
// sticky `#navbar`'s bottom edge (the navbar always draws over board content). No
// ancestor-clip walk — a `position:fixed` card escapes every ancestor's own `overflow`
// clipping (confirmed no ancestor between a card and <body> sets `transform`/`filter`/
// `perspective`, which are the only things that would create a containing block short of
// the viewport), so an inner scroll container's own bounds are irrelevant here.
export function visibleBoundsFor() {
  const bounds = { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight };
  const navbar = document.getElementById('navbar');
  if (navbar) {
    const navStyle = window.getComputedStyle(navbar);
    if (navStyle.position === 'sticky' || navStyle.position === 'fixed') {
      bounds.top = Math.max(bounds.top, navbar.getBoundingClientRect().bottom);
    }
  }
  return bounds;
}

// Pure: normalise a wheel delta to px. deltaMode 0 = px, 1 = lines, 2 = pages (`pageSize`
// is the scroller's visible extent on that axis).
export function wheelDeltaPx(delta, deltaMode, pageSize = 800) {
  if (deltaMode === 1) return delta * LINE_PX;
  if (deltaMode === 2) return delta * pageSize;
  return delta;
}

// Pure: can an element with these scroll metrics scroll any further toward `delta`
// (positive = down)? 1px tolerance for fractional scroll positions.
export function canScrollInDirection({ scrollTop, scrollHeight, clientHeight }, delta) {
  const max = scrollHeight - clientHeight;
  if (max <= 1 || !delta) return false;
  return delta > 0 ? scrollTop < max - 1 : scrollTop > 0;
}

function isDragOrPanActive(container) {
  if (document.querySelector('.sb-card-dragging, .kanban-card-dragging, .kanban-column-dragging')) return true;
  return !!container.closest('.kanban-board-panning') || container.classList.contains('kanban-board-panning');
}

// Turn `cardEl` into a position:fixed overlay sized to its own un-clamped content.
// Synchronous start to finish — every intermediate mutation (placeholder insert, width
// grow, scrollHeight read) happens before the browser's next paint, so nothing but the
// final state is ever visible.
export function expandCard(cardEl, container) {
  if (cardEl.classList.contains(EXPANDED_CLASS)) return;
  if (container && isDragOrPanActive(container)) return;

  const rect = cardEl.getBoundingClientRect();
  const bounds = visibleBoundsFor();

  const placeholder = document.createElement('div');
  placeholder.className = PLACEHOLDER_CLASS;
  placeholder.style.height = rect.height + 'px';
  cardEl.parentNode.insertBefore(placeholder, cardEl);
  cardEl._hoverExpandPlaceholder = placeholder;

  cardEl.classList.add(EXPANDED_CLASS, FOCUSED_CLASS);

  // Still position:static here — growing the width in place is a same-subtree reflow
  // (may visually overflow its grid track/column by a few px), not yet painted.
  const width = rect.width + GROW_X;
  cardEl.style.width = width + 'px';

  // Now reflects the un-clamped title/description (see styles.css's .task-card-expanded
  // descendant rules) at the grown width. Capped to the visible bounds so a pathological
  // description can't push the card off-screen — it scrolls internally instead.
  const maxHeight = Math.max(rect.height, bounds.bottom - bounds.top - 2 * MARGIN);
  const naturalHeight = Math.min(cardEl.scrollHeight, maxHeight);

  const { left, top } = computeExpandedOrigin(rect, bounds, width, naturalHeight);

  cardEl.style.position = 'fixed';
  cardEl.style.left = left + 'px';
  cardEl.style.top = top + 'px';
  cardEl.style.height = naturalHeight + 'px';
  cardEl.style.overflowY = naturalHeight < cardEl.scrollHeight ? 'auto' : '';
}

// Returns true when it actually collapsed an expanded card (callers that re-measure the
// card's resting rect — a drag start — only need to when this is true).
export function collapseCard(cardEl) {
  if (!cardEl.classList.contains(EXPANDED_CLASS)) return false;
  cardEl.classList.remove(EXPANDED_CLASS);
  // A clicked (pinned) card keeps its ring after the pointer leaves — see SELECTED_CLASS above.
  if (!cardEl.classList.contains(SELECTED_CLASS)) cardEl.classList.remove(FOCUSED_CLASS);
  cardEl.style.position = '';
  cardEl.style.left = '';
  cardEl.style.top = '';
  cardEl.style.width = '';
  cardEl.style.height = '';
  cardEl.style.overflowY = '';
  const placeholder = cardEl._hoverExpandPlaceholder;
  if (placeholder) {
    placeholder.remove();
    cardEl._hoverExpandPlaceholder = null;
  }
  return true;
}

// Drop the pin (and the ring, unless the card is expanded right now — a hovered card keeps its
// ring until it collapses) from every selected card under `container`. Null-safe: a page's
// board element may already be gone when the drawer's deferred onClose fires.
export function clearSelectedCard(container) {
  if (!container) return;
  container.querySelectorAll('.' + SELECTED_CLASS).forEach((card) => {
    card.classList.remove(SELECTED_CLASS);
    if (!card.classList.contains(EXPANDED_CLASS)) card.classList.remove(FOCUSED_CLASS);
  });
}

// Pin `cardEl` as the one clicked card under `container`: at most one card per board is
// selected, so a previous pin is cleared first. The ring stays until clearSelectedCard().
export function selectCard(cardEl, container) {
  if (!cardEl) return;
  clearSelectedCard(container);
  cardEl.classList.add(SELECTED_CLASS, FOCUSED_CLASS);
}

// ── Board focus behind the task-detail drawer (TPT378) ──
//
// While the drawer is open the clicked card stays sharp + ringed and the rest of the board is
// blurred (styles.css `.board-has-focus`). `board` is the persistent board element the page
// renders into (`#kanban-board` / `#sprint-board`) — it outlives every innerHTML re-render, so
// the class lives there while the pinned card is re-marked by key after each repaint. All
// helpers are null-safe: a page's board may already be gone when the drawer's callbacks fire.
const BOARD_FOCUS_CLASS = 'board-has-focus';

// Nearest card under `board` carrying `taskKey`, else null. Compares `dataset.taskKey` rather
// than building an attribute selector, so no key needs escaping.
export function findTaskCard(board, taskKey) {
  if (!board || taskKey == null) return null;
  const cards = board.querySelectorAll('.task-card');
  for (const card of cards) {
    if (card.dataset && card.dataset.taskKey === String(taskKey)) return card;
  }
  return null;
}

// Focus `cardEl` and blur the rest of `board`. A clicked card is normally hover-expanded (a
// position:fixed overlay) by now, so it's collapsed back into its slot first — the ring and
// the z-lift in styles.css then apply to a card in its resting layout.
export function focusBoardCard(cardEl, board) {
  if (!cardEl || !board) return;
  collapseCard(cardEl);
  selectCard(cardEl, board);
  board.classList.add(BOARD_FOCUS_CLASS);
}

// Undo focusBoardCard(): drop the board class and the pinned card's ring.
export function clearBoardFocus(board) {
  if (!board) return;
  board.classList.remove(BOARD_FOCUS_CLASS);
  clearSelectedCard(board);
}

// Nearest ancestor of `start` that can actually scroll on `axis`, else the document's
// scrolling element. A `position:fixed` expanded card's own scroll chain ends at the
// viewport, so a wheel over it never reaches the column/board scroller its placeholder sits
// in — this finds that scroller so the wheel can be handed to it.
function scrollerFor(start, axis) {
  for (let el = start; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
    const cs = window.getComputedStyle(el);
    const overflow = axis === 'y' ? cs.overflowY : cs.overflowX;
    const overflows = axis === 'y' ? el.scrollHeight > el.clientHeight : el.scrollWidth > el.clientWidth;
    if ((overflow === 'auto' || overflow === 'scroll') && overflows) return el;
  }
  return document.scrollingElement;
}

// Delegate capture-phase `mouseenter`/`mouseleave` on `container` for every element
// matching `selector` (neither event bubbles, so delegation needs the capture phase) —
// survives every `innerHTML` re-render both board pages do on almost any data change,
// unlike a listener bound per rendered card. A short dwell before expanding and a
// shorter one before collapsing smooths out a fast mouse pass across the grid; every
// deferred callback re-checks the card is still attached first, since a re-render can
// wipe the container out from under a pending timer.
//
// Besides hover, three gestures keep an overlay from misbehaving (all kanban-driven, all
// harmless on the sprint board):
//  - pointer held: no card expands while a mouse button is down, so a press/drag/pan/column
//    drag never starts from — or hovers into — an overlay, even before the drag threshold.
//  - scroll: a `position:fixed` overlay doesn't move with its column's scroller, so any
//    scroll collapses it and holds further expands until scrolling settles.
//  - wheel: over an overlay the wheel would chain to the viewport, not the column its
//    placeholder sits in, so it's redirected to that scroller (and the overlay collapses).
export function bindHoverExpand(container, selector = '.task-card') {
  if (!container) return () => {};
  const expandTimers = new WeakMap();
  const collapseTimers = new WeakMap();
  let pointerHeld = false;
  let suppressUntil = 0;

  // Re-arms itself while a scroll is still settling, so a card the pointer is resting on
  // still expands once the scroll stops instead of waiting for the pointer to leave and
  // re-enter. A held pointer just drops the expand (a click/drag owns that gesture).
  const armExpand = (card, delay) => {
    clearTimeout(expandTimers.get(card));
    expandTimers.set(card, setTimeout(() => {
      if (!document.body.contains(card) || pointerHeld) return;
      const wait = suppressUntil - Date.now();
      if (wait > 0) { armExpand(card, wait); return; }
      expandCard(card, container);
    }, delay));
  };

  const onEnter = (e) => {
    const card = e.target.closest ? e.target.closest(selector) : null;
    if (!card || !container.contains(card)) return;
    if (e.buttons === 0) pointerHeld = false; // resync if a mouseup landed outside the window
    clearTimeout(collapseTimers.get(card));
    armExpand(card, EXPAND_DELAY);
  };

  const onLeave = (e) => {
    const card = e.target.closest ? e.target.closest(selector) : null;
    if (!card || !container.contains(card)) return;
    clearTimeout(expandTimers.get(card));
    clearTimeout(collapseTimers.get(card));
    collapseTimers.set(card, setTimeout(() => {
      if (document.body.contains(card)) collapseCard(card);
    }, COLLAPSE_DELAY));
  };

  const onMouseDown = (e) => { if (e.button === 0) pointerHeld = true; };
  const onMouseUp = () => { pointerHeld = false; };

  const onScroll = (e) => {
    const t = e.target;
    // An over-tall overlay scrolls its own content (expandCard() sets overflowY:auto) —
    // that isn't the board moving under it.
    if (t && t.nodeType === 1 && t.closest('.' + EXPANDED_CLASS)) return;
    suppressUntil = Date.now() + SCROLL_SETTLE;
    container.querySelectorAll('.' + EXPANDED_CLASS).forEach(collapseCard);
  };

  const onWheel = (e) => {
    if (e.ctrlKey) return; // pinch-zoom, not a scroll
    const card = e.target.closest ? e.target.closest('.' + EXPANDED_CLASS) : null;
    if (!card || !container.contains(card)) return;

    const dy = wheelDeltaPx(e.deltaY, e.deltaMode, card.clientHeight);
    // The overlay's own scroll wins while it still has room in that direction.
    if (card.style.overflowY === 'auto' && canScrollInDirection(card, dy)) return;

    const dx = wheelDeltaPx(e.deltaX, e.deltaMode, card.clientWidth);
    const anchor = card._hoverExpandPlaceholder || card;
    const scrollerY = dy ? scrollerFor(anchor.parentElement, 'y') : null;
    const scrollerX = dx ? scrollerFor(anchor.parentElement, 'x') : null;
    collapseCard(card);
    suppressUntil = Date.now() + SCROLL_SETTLE;
    if (!scrollerY && !scrollerX) return;
    e.preventDefault();
    if (scrollerY) scrollerY.scrollBy(0, dy);
    if (scrollerX) scrollerX.scrollBy(dx, 0);
  };

  container.addEventListener('mouseenter', onEnter, true);
  container.addEventListener('mouseleave', onLeave, true);
  container.addEventListener('wheel', onWheel, { capture: true, passive: false });
  document.addEventListener('mousedown', onMouseDown, true);
  document.addEventListener('mouseup', onMouseUp, true);
  window.addEventListener('scroll', onScroll, { capture: true, passive: true });
  return () => {
    container.removeEventListener('mouseenter', onEnter, true);
    container.removeEventListener('mouseleave', onLeave, true);
    container.removeEventListener('wheel', onWheel, { capture: true });
    document.removeEventListener('mousedown', onMouseDown, true);
    document.removeEventListener('mouseup', onMouseUp, true);
    window.removeEventListener('scroll', onScroll, { capture: true });
  };
}
